// Tags of the special actions. This version identifies them by a link type of their own,
// the analytics already use the tag of the versions where the link type is `action`.
const LINK_ACTION_TAGS = {
    back: `browser.navigation.back`,
    next: `browser.navigation.next`,
    offline: `browser.navigation.reload`,
    wheelchair: `display.accessibility.wheelchair`,
    language: `locale.current.select`,
    collapse: `ui.menu.collapse`,
    feedback: `ui.feedback.toggle`
};

// The stable id of the block, not getId() which is the custom id when there is one: it can be renamed and isn't unique
function toBlockSummary(block) {
    return {id: block.id, type: block.type};
}

/**
 * The analytics action of a click on a CMS link, as described by CmsAnalyticsActionModel.
 * @param {object} params
 * @param {CmsLink} params.cmsLink
 * @param {string} [params.destination] Path, url, phone number, email... the link leads to
 * @param {boolean} params.opensInNewTab
 * @param {CmsPage} params.page Page where the link is
 * @param {string} params.path Current path of the page
 * @param {{block: CmsBlock, parent: CmsBlock}} [params.blockContext] Block containing the link, and its parent block
 * @param {CmsItem} [params.boundItem] Item the link is about, when it is bound to one (ex: an item of a list)
 * @returns {object|null} null when the link leads nowhere (ex: the empty link every block that can be a link has by default)
 */
export function createLinkClickAction({cmsLink, destination, opensInNewTab, page, path, blockContext, boundItem}) {
    // A link that leads nowhere is not a link click. CmsLink.valid isn't reliable alone: special actions like
    // language are never valid, so a link with a destination counts too (tel and mailto always have one, even empty)
    const actionTag = LINK_ACTION_TAGS[cmsLink.type];
    const hasDestination = typeof destination === `string` && destination !== `` && ![`tel`, `mailto`].includes(cmsLink.type);
    if (!actionTag && !cmsLink.valid && !hasDestination) {
        return null;
    }

    const target = {};
    if (actionTag) {
        target.type = `action`;
        target.action = actionTag;
    } else {
        target.type = cmsLink.type;
    }
    if (destination) {
        target.to = destination;
    }
    target.openInNewTab = opensInNewTab;

    const action = {
        type: `click`,
        page: {id: page.id, permalink: path, name: page.name},
        target
    };

    if (blockContext?.block) {
        action.block = toBlockSummary(blockContext.block);
        if (blockContext.parent) {
            action.block.parent = toBlockSummary(blockContext.parent);
        }
    }

    // The item a link leads to is the one selected, otherwise the item the link is bound to
    const cmsItem = cmsLink.type === `item` ? cmsLink.getItem() : boundItem;
    if (cmsItem) {
        action.cmsItem = {id: cmsItem.id, type: cmsItem.type, name: cmsItem.getName()};
    }

    return action;
}
